/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, { memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { compact, uniqueId } from 'lodash';
import { getTimeZone } from '_entities/Auth/Auth.selectors';
import LinkCell from 'modules/old/cells/LinkCell';
import HeaderCell from 'modules/old/cells/HeaderCell';
import TextCell from 'modules/old/cells/TextCell';
import TimeCell from 'modules/old/cells/TimeCell';
import TableColumnControl from 'ui-kit/old/TableColumnControl';
import useSortTable from 'utils/old/useSortTable';
import RestoreAuditLogCell from 'modules/old/cells/RestoreAuditLogCell';
import { COLUMNS, INITIAL_COLUMNS } from './constants';
import TableWrapper from '../../ui-kit/TableWrapper';

const getInitialColumns = ({
  handleSort,
  sortOption,
  dumpedActiveColumns,
  tableControlParams,
  editCellParams,
  isShortVersion,
  timeZone,
}) =>
  compact([
    dumpedActiveColumns.user__email && {
      Header: () => (
        <HeaderCell col="User ID" sortBy="user__email" sortOption={sortOption} handleSort={handleSort('user__email')} />
      ),
      width: 300,
      accessor: 'user__email',
      Cell: ({ value, original }) => <LinkCell id={uniqueId()} text={value} to={`/users/${original.user__id}`} />,
    },
    dumpedActiveColumns.user__agency_default__name && {
      Header: () => (
        <HeaderCell
          col="Agency"
          sortBy="user__agency_default__name"
          sortOption={sortOption}
          handleSort={handleSort('user__agency_default__name')}
        />
      ),
      minWidth: 140,
      accessor: 'user__agency_default__name',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    dumpedActiveColumns.full_name && {
      Header: () => (
        <HeaderCell col="User Name" sortBy="full_name" sortOption={sortOption} handleSort={handleSort('full_name')} />
      ),
      minWidth: 140,
      accessor: 'full_name',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    dumpedActiveColumns.object_type &&
      !isShortVersion && {
        Header: () => (
          <HeaderCell
            col="Object Type"
            sortBy="object_type"
            sortOption={sortOption}
            handleSort={handleSort('object_type')}
          />
        ),
        minWidth: 140,
        accessor: 'object_type',
        Cell: ({ value }) => <TextCell text={value} />,
      },
    dumpedActiveColumns.record_id &&
      !isShortVersion && {
        Header: () => (
          <HeaderCell col="Record ID" sortBy="record_id" sortOption={sortOption} handleSort={handleSort('record_id')} />
        ),
        minWidth: 140,
        accessor: 'record_id',
        Cell: ({ value }) => <TextCell text={value} />,
      },
    dumpedActiveColumns.field_name && {
      Header: () => (
        <HeaderCell
          col="Field Name"
          sortBy="field_name"
          sortOption={sortOption}
          handleSort={handleSort('field_name')}
        />
      ),
      minWidth: 140,
      accessor: 'field_name',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    dumpedActiveColumns.action_flag && {
      Header: () => (
        <HeaderCell
          col="Action type"
          sortBy="action_flag"
          sortOption={sortOption}
          handleSort={handleSort('action_flag')}
        />
      ),
      minWidth: 140,
      accessor: 'action_flag',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    dumpedActiveColumns.previous_value_display && {
      Header: () => (
        <HeaderCell
          col="Previous Value"
          sortBy="previous_value_display"
          sortOption={sortOption}
          handleSort={handleSort('previous_value_display')}
        />
      ),
      minWidth: 180,
      accessor: 'previous_value_display',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    dumpedActiveColumns.new_value_display && {
      Header: () => (
        <HeaderCell
          col="New Field Value"
          sortBy="new_value_display"
          sortOption={sortOption}
          handleSort={handleSort('new_value_display')}
        />
      ),
      minWidth: 180,
      accessor: 'new_value_display',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    dumpedActiveColumns.action_datetime && {
      Header: () => (
        <HeaderCell
          col="Audit Log Date"
          sortBy="action_datetime"
          sortOption={sortOption}
          handleSort={handleSort('action_datetime')}
        />
      ),
      minWidth: 200,
      accessor: 'action_datetime',
      Cell: ({ value }) => <TimeCell isFullDate data={value} timezone={timeZone} />,
    },
    dumpedActiveColumns.submitted && {
      Header: () => (
        <HeaderCell col="Submitted" sortBy="submitted" sortOption={sortOption} handleSort={handleSort('submitted')} />
      ),
      minWidth: 200,
      accessor: 'submitted',
      Cell: ({ value }) => <TimeCell isFullDate data={value} timezone={timeZone} />,
    },
    dumpedActiveColumns.import_action && {
      Header: () => (
        <HeaderCell
          col="Entry Type"
          sortBy="import_action"
          sortOption={sortOption}
          handleSort={handleSort('import_action')}
        />
      ),
      minWidth: 140,
      accessor: 'import_action',
      Cell: ({ value }) => <TextCell text={value} />,
    },
    !isShortVersion && {
      Header: () => <TableColumnControl {...tableControlParams} isAuditPage />,
      width: 56,
      accessor: 'restore',
      Cell: ({ original }) => <RestoreAuditLogCell original={original} {...editCellParams} />,
    },
  ]);

const AuditLogsTable = ({ data, sortHandler, sortOption, handleAuditAction, isShortVersion = false, footer }) => {
  const handleSort = useSortTable(sortOption, sortHandler);
  const timeZone = useSelector(getTimeZone);
  const [activeColumns, setActiveColumns] = useState(INITIAL_COLUMNS);
  const [isOpen, setIsOpen] = useState(false);

  const dumpedActiveColumns = useMemo(
    () => activeColumns.reduce((acc, value) => ({ ...acc, [value]: value }), {}),
    [activeColumns],
  );

  const tableControlParams = useMemo(
    () => ({
      activeColumns,
      columns: COLUMNS,
      setActiveColumns,
      setIsOpen,
      isOpen,
    }),
    [activeColumns, isOpen],
  );

  const editCellParams = useMemo(
    () => ({
      auditAction: handleAuditAction,
    }),
    [handleAuditAction],
  );

  const wrappedColumns = useMemo(
    () =>
      getInitialColumns({
        handleSort,
        sortOption,
        dumpedActiveColumns,
        tableControlParams,
        editCellParams, // eslint-disable-next-line
        isShortVersion,
        timeZone,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dumpedActiveColumns, sortOption, tableControlParams, handleSort, isShortVersion, timeZone],
  );

  return (
    <>
      <TableWrapper
        columns={wrappedColumns}
        data={data || []}
        tableControlParams={tableControlParams}
        isEdit
        editCellParams={editCellParams}
        isAuditPage
      />
      {/* The pagination used to be a sibling of this component; taking it as a prop lets the
          new-design table put it inside its card instead. Rendered here in exactly the position it
          already had, so the legacy layout is unchanged. */}
      {footer}
    </>
  );
};

AuditLogsTable.defaultProps = {};

export default memo(AuditLogsTable);
